<script setup lang="ts">
import { ref, watch } from 'vue'
import Modal from '@/components/ui/Modal.vue'
import { extractError } from '@/api/client'
import { useUserStore } from '@/stores/user'
import { useKeysStore } from '@/stores/keys'
import {
  DEFAULT_FINGERPRINT,
  FINGERPRINT_OPTIONS,
  FINGERPRINT_RISK_COPY,
  RISK_CONSENT_YES,
  requiresRiskConsent,
  type RiskFingerprint,
} from '@/utils/fingerprint'

const user = useUserStore()
const keys = useKeysStore()

const selected = ref<string>(DEFAULT_FINGERPRINT)
const status = ref('')
const statusClass = ref('text-muted')
const saving = ref(false)
const infoOpen = ref(false)
const riskOpen = ref(false)
const riskKind = ref<RiskFingerprint>('random')
const acceptingRisk = ref(false)

function storedFingerprint(): string {
  return user.profile?.global_fingerprint || DEFAULT_FINGERPRINT
}

watch(
  () => user.profile?.global_fingerprint,
  (value) => {
    if (riskOpen.value || saving.value) return
    if (value) selected.value = value
  },
  { immediate: true },
)

function onFingerprintChange() {
  if (!requiresRiskConsent(selected.value)) {
    void save(false)
    return
  }
  riskKind.value = selected.value as RiskFingerprint
  riskOpen.value = true
}

function cancelRisk() {
  if (acceptingRisk.value) return
  riskOpen.value = false
  selected.value = storedFingerprint()
}

async function confirmRisk() {
  acceptingRisk.value = true
  riskOpen.value = false
  try {
    await save(true)
  } finally {
    acceptingRisk.value = false
  }
}

async function save(withConsent: boolean) {
  if (saving.value) return
  if (requiresRiskConsent(selected.value) && !withConsent) return
  const previous = storedFingerprint()
  saving.value = true
  status.value = 'Сохранение...'
  statusClass.value = 'text-muted'
  try {
    const res = await keys.setGlobalFingerprint(
      selected.value,
      withConsent ? RISK_CONSENT_YES : '',
    )
    if (!res.success) {
      selected.value = previous
      status.value = res.message || 'Ошибка'
      statusClass.value = 'text-danger'
      return
    }
    const saved = res.global_fingerprint ?? selected.value
    if (user.profile) {
      user.profile.global_fingerprint = saved
    }
    selected.value = saved
    status.value = res.changed ? 'Сохранено' : 'Без изменений'
    statusClass.value = 'text-success'
    if (res.changed) {
      void keys.fetch(true)
    }
  } catch (e) {
    selected.value = previous
    status.value = extractError(e, 'Не удалось сохранить отпечаток')
    statusClass.value = 'text-danger'
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div v-if="user.profile" class="panel flex h-full flex-col gap-3 !py-4 !px-5">
    <h3 class="flex items-center gap-2 text-sm font-bold">
      Глобальный fingerprint
      <button
        type="button"
        class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-white-dark/50 text-xs font-bold text-white-dark transition hover:border-primary hover:text-primary"
        aria-label="Справка о fingerprint"
        title="Справка"
        @click="infoOpen = true"
      >
        ?
      </button>
    </h3>
    <select
      v-model="selected"
      class="form-select w-full"
      :disabled="saving || riskOpen"
      @change="onFingerprintChange"
    >
      <option v-for="opt in FINGERPRINT_OPTIONS" :key="opt" :value="opt">
        {{ opt }}
      </option>
    </select>
    <small v-if="status" class="text-xs" :class="statusClass">
      {{ status }}
    </small>
  </div>

  <Modal
    :open="infoOpen"
    title="Отпечаток браузера (Fingerprint)"
    max-width="lg"
    @close="infoOpen = false"
  >
    <div class="space-y-4 text-sm leading-relaxed text-[#1f2937]">
      <p>
        При каждом подключении ваш клиент «представляется» серверу и системам глубокой инспекции трафика (DPI).
        Настройка Fingerprint позволяет сделать так, чтобы соединение выглядело как обычный браузер, а не как VPN‑клиент.
      </p>

      <div>
        <p class="mb-2 font-semibold">Доступные отпечатки</p>
        <ul class="list-disc space-y-1.5 pl-5">
          <li>
            <strong>Chrome, Firefox, Safari, Edge</strong> - имитируют соответствующие настольные браузеры
          </li>
          <li>
            <strong>iOS, Android</strong> - имитируют мобильные браузеры: используют характерные для смартфонов наборы шифров (cipher suites), порядок TLS‑параметров, ALPN и подпись ClientHello
          </li>
          <li>
            <strong>360, QQ</strong> - браузеры для китайского рынка; перед использованием рекомендуется проверить их работу в вашей сети
          </li>
          <li>
            <strong>random</strong> — {{ FINGERPRINT_RISK_COPY.random.body }}
            {{ FINGERPRINT_RISK_COPY.random.consequences }}
          </li>
          <li>
            <strong>randomized</strong> — {{ FINGERPRINT_RISK_COPY.randomized.body }}
            {{ FINGERPRINT_RISK_COPY.randomized.consequences }}
          </li>
        </ul>
      </div>

      <div>
        <p class="mb-2 font-semibold">Важные замечания</p>
        <ul class="list-disc space-y-1.5 pl-5">
          <li>
            Не выбирайте <strong>Chrome</strong>, если вы в России. С мая 2026 года российская система DPI эффективно распознаёт и блокирует Chrome‑отпечатки.
          </li>
          <li>
            После смены отпечатка подождите не менее <strong>60 секунд</strong> перед повторным подключением. С мая 2026 года DPI фиксирует повторные попытки; если подключиться раньше чем через 60 секунд после блокировки, соединение может быть заблокировано на 10 минут.
          </li>
          <li>
            После выбора настройка сохраняется автоматически. Ключи обновляются в фоновом режиме или вручную - по кнопке «Пересобрать ключи». Применяется ко всем ключам, поддерживающим отпечаток: VLESS/Trojan Reality, TLS, Hysteria.
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn btn-primary" @click="infoOpen = false">
        Закрыть
      </button>
    </template>
  </Modal>

  <Modal
    :open="riskOpen"
    :title="FINGERPRINT_RISK_COPY[riskKind].title"
    max-width="lg"
    @close="cancelRisk"
  >
    <div class="space-y-4 text-sm leading-relaxed text-[#1f2937]">
      <p>{{ FINGERPRINT_RISK_COPY[riskKind].body }}</p>
      <div>
        <p class="mb-2 font-semibold">Возможные последствия</p>
        <p>{{ FINGERPRINT_RISK_COPY[riskKind].consequences }}</p>
      </div>
    </div>
    <template #footer>
      <button type="button" class="btn btn-outline-primary" @click="cancelRisk">
        Отмена
      </button>
      <button type="button" class="btn btn-primary" :disabled="saving" @click="confirmRisk">
        Понимаю риски
      </button>
    </template>
  </Modal>
</template>
